// "A new version is available" toast.
//
// vite-plugin-pwa registers the service worker with registerType 'prompt', so a
// freshly deployed build is downloaded but NOT activated until the user opts in.
// useRegisterSW surfaces that moment as `needRefresh`; accepting calls
// updateServiceWorker(true), which activates the waiting worker and reloads the
// page onto the new build.
//
// Detecting a deploy on an ALREADY-OPEN client is the subtle part. The browser
// only re-fetches sw.js (and so only compares the precache manifest hash that
// flips needRefresh) on a hard navigation or an explicit registration.update().
// A SPA tab stays open for hours and navigates only client-side via React
// Router — neither triggers that check — so without prompting it ourselves a
// deployed build is never noticed. So we capture the registration and, in an
// effect below, install the standard vite-plugin-pwa periodic-update poll plus
// a refocus check — the toast then appears within UPDATE_CHECK_INTERVAL_MS of a
// deploy (or immediately when the user returns to the tab). /sw.js is served
// no-cache (staticwebapp.config.json) so each update() fetches the fresh
// worker. No backend coordination. The poll lives in a useEffect (not the
// onRegisteredSW callback) so the interval + listener are cleaned up on unmount
// and StrictMode's dev double-mount can't stack duplicates.
//
// Rendered once from AppShell (inside the authed shell) through the shared
// ToastHost, so it matches the rest of the app's toasts exactly — same
// bottom-right placement AND same text size. It used to hand-roll its own
// Notification, which meant it inherited Kendo's smaller default font and read
// as less important than every other toast (AAI-68). It opts out of ToastHost's
// 10s auto-dismiss: the user has to be able to find the Reload button.

import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@progress/kendo-react-buttons';
import { ToastHost } from './ToastHost';

// How often an open tab polls for a new build. 15 min balances promptness
// against needless sw.js fetches; the refocus check covers the gap in between.
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

export function UpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | undefined>();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW: (_swUrl, r) => setRegistration(r),
  });

  useEffect(() => {
    if (!registration) return;
    // Skip the network probe when offline — update() would just fail. Swallow a
    // rejected update() (transient network error) so it isn't an unhandled
    // rejection; the next tick simply tries again.
    const check = () => {
      if (navigator.onLine) void registration.update().catch(() => {});
    };
    const intervalId = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    // Returning to a backgrounded tab is the most common "I left this open
    // across a deploy" moment — check immediately rather than waiting out the
    // interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [registration]);

  return (
    <ToastHost
      // A stable string literal, so ToastHost's effect doesn't re-run per render.
      toast={needRefresh ? 'A new version is available.' : null}
      onClose={() => setNeedRefresh(false)}
      autoDismissMs={null}
      action={
        /* updateServiceWorker(true) activates the waiting SW and reloads. */
        <Button size="small" themeColor="primary" onClick={() => void updateServiceWorker(true)}>
          Reload
        </Button>
      }
    />
  );
}
